<template>
  <div class="main-container">
    <div class="title-container">
      <capusta-title :title="merchantName" icon-outside />
    </div>
    <div class="content-container">
      <div
        v-if="localization"
        class="option-container"
        @click="handleShownLocalizationList"
      >
        <option-panel :option="localization" noactive>
          <div slot="icon" :class="$style.iconLocalization">
            <country-flag :language-code="localization.code" />
          </div>
          <cs-button type="secondary">
            {{ $t('common.change') }}
          </cs-button>
        </option-panel>
      </div>
      <div class="option-container">
        <capusta-panel
          v-if="isNeedIdentification || isApproved"
          icon="icon-identity-white"
          :description="$t('common.identification')"
          @click="goToIdentity"
        />
      </div>
      <div class="option-container">
        <capusta-panel
          icon="icon-eagle-white"
          :description="selfEmployedTitle"
          @click="goToSelfEmployed"
        />
      </div>
      <div class="option-container">
        <capusta-panel
          icon="icon-key-white"
          :description="$t('account.secure')"
          @click="goToTwoFactor"
        />
      </div>
      <div class="option-container">
        <capusta-panel
          icon="icon-time-white"
          :description="$t('common.timezone')"
          @click="goToTimeZone"
        />
      </div>
    </div>

    <languages-list-modal
      v-model="isShownLocalizationList"
      :languages="formattedLanguages"
      @change-localization="handleChangeLocalization"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import HTTPService from '@/services/Http';
import { getLanguages } from 'services/localization';

import CapustaTitle from 'modules/CapustaLayout/CapustaTitle';
import CapustaPanel from 'modules/CapustaLayout/CapustaPanel';
import OptionPanel from '@/components/capusta/OptionPanel';

import CountryFlag from 'molecules/localization/CountryFlag';
import LanguagesListModal from 'organisms/localization/LanguagesListModal';

import { CABINET, IDENTITY, TWO_FACTOR, TIMEZONE } from '@/router/const';

export default {
  name: 'MerchantProfile',
  components: {
    CapustaTitle,
    CapustaPanel,
    OptionPanel,
    CountryFlag,
    LanguagesListModal,
  },
  created() {
    this.loadData();
  },
  data() {
    return {
      languagesList: [],
      isShownLocalizationList: false,
    };
  },
  computed: {
    merchantName() {
      return this.MERCHANT_INFO.name;
    },
    selfEmployedTitle() {
      return this.isSelfEmployedActivated ? this.$t('declaration.of.transfers') : this.$t('turn.on.self.employed');
    },
    formattedLanguages() {
      if (this.$validate.isNull(this.localization)) {
        return this.languagesList;
      }

      return this.languagesList.map(language => {
        return {
          ...language,
          active: language.code === this.localization.code,
          description: this.$t(language.description),
        };
      });
    },
    localization() {
      const { localization } = this.MERCHANT_INFO;

      if (this.$validate.isNull(localization)) {
        return null;
      }

      return {
        ...localization,
        description: localization.title,
        label: this.$t('settings.cabinet.localization'),
      };
    },
    ...mapState(['MERCHANT_INFO']),
    ...mapGetters(['isSelfEmployedActivated', 'isObsidianTheme', 'isNeedIdentification']),
    ...mapGetters('identity', ['isApproved']),
  },
  methods: {
    loadData() {
      this.loadLanguages();
      this.fetchIdentification();
    },
    async loadLanguages() {
      try {
        this.languagesList = await getLanguages({ type: 'cabinet' });
      } catch (error) {
        console.error(error);
      }
    },
    goToCabinet() {
      this.$router.push({ name: CABINET });
    },
    goToIdentity() {
      this.$router.push({ name: IDENTITY });
    },
    goToSelfEmployed() {
      this.$router.push({ name: 'selfEmployed' });
    },
    goToTwoFactor() {
      this.$router.push({ name: TWO_FACTOR });
    },
    goToTimeZone() {
      this.$router.push({ name: TIMEZONE });
    },
    handleShownLocalizationList() {
      this.isShownLocalizationList = true;
    },
    async handleChangeLocalization(data) {
      const language = {
        code: data.code,
        description: data.description,
        id: data.id,
        title: data.title,
      };

      try {
        await HTTPService.updateMerchant({
          ...this.MERCHANT_INFO,
          localization: language,
        });

        this.MERCHANT_INFO.localization = language;
        this.$i18n.locale = language.code.toLowerCase();

        this.$alert({
          type: 'success',
          text: this.$t('common.success'),
        });
      } catch (error) {
        console.log(error);

        this.$alert({
          type: 'warning',
          text: this.$t('error.in.system'),
        });
      }
    },
    ...mapActions('identity', [
      'fetchIdentification',
    ]),
  },
};
</script>

<style lang="sass" module>
  .iconLocalization
    width: 40px
    height: 40px
    display: flex
    align-items: center
    justify-content: center
    margin-right: 20px
    font-weight: 600
    font-size: 20px
    line-height: 24px
    color: #1E1D2F
    background-color: #181722
    border-radius: 50%
</style>

<style lang="sass" scoped>
  .main-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto
    padding: 20px 0px

    +respond-to('small')
      padding: 40px 0

    +respond-to('medium')
      padding: 70px 0

  .title-container
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .option-container
    &:not(:last-child)
      margin-bottom: 10px
</style>
